import {
  createThemes,
  DEFAULT_PALETTE,
  DEFAULT_SUBDUED_PALETTE,
  type Palette,
} from '@bsky.app/alf'

/**
 * astrabun.com brand override: mint/teal hue (~163deg), re-anchored so
 * `primary_500` keeps enough contrast against white for solid button fills.
 * Upstream Bluesky's `#006AFF` is ~4.65:1 against white; this teal is
 * ~5.06:1. Saturation is pushed hard (up to ~95%) so the accent reads as
 * vividly as astrabun.com's own mint in borders/icons/hover states, while
 * `primary_500` itself stays dark enough to carry white button text.
 * `primary_300`/`primary_400` land close to astrabun.com's literal mint
 * (`#6df0c9`) so it reads as "the astra color" in lighter-weight UI.
 */
const BUNSKY_PRIMARY: Pick<
  Palette,
  | 'primary_25'
  | 'primary_50'
  | 'primary_100'
  | 'primary_200'
  | 'primary_300'
  | 'primary_400'
  | 'primary_500'
  | 'primary_600'
  | 'primary_700'
  | 'primary_800'
  | 'primary_900'
  | 'primary_950'
  | 'primary_975'
> = {
  primary_25: '#F2FDFA',
  primary_50: '#E4FBF5',
  primary_100: '#C7F9EB',
  primary_200: '#96F8DC',
  primary_300: '#59F8CB',
  primary_400: '#06E5A6',
  primary_500: '#077E5C',
  primary_600: '#06654A',
  primary_700: '#07503B',
  primary_800: '#074030',
  primary_900: '#083025',
  primary_950: '#08211A',
  primary_975: '#071713',
}

/** astrabun.com's accent pink, replacing Bluesky's default like-heart pink. */
const BUNSKY_PINK = '#F9B0D8'

/**
 * astrabun.com navy tint for the neutral/surface ramp. Bluesky's own
 * `contrast_*` ramp already leans blue-navy (e.g. `#19222E`); this pushes
 * saturation up (~1.9x) at astrabun.com's navy hue (~211deg) so surfaces,
 * borders, and dividers read as astra's navy rather than Bluesky's slate.
 * `contrast_0` (pure white, used as light-theme bg and inverted-text-on-fill)
 * is deliberately left untouched everywhere it appears.
 */
const BUNSKY_CONTRAST: Pick<
  Palette,
  | 'contrast_25'
  | 'contrast_50'
  | 'contrast_100'
  | 'contrast_200'
  | 'contrast_300'
  | 'contrast_400'
  | 'contrast_500'
  | 'contrast_600'
  | 'contrast_700'
  | 'contrast_800'
  | 'contrast_900'
  | 'contrast_950'
  | 'contrast_975'
  | 'contrast_1000'
> = {
  contrast_25: '#F8FAFC',
  contrast_50: '#ECF2F9',
  contrast_100: '#D6E3F0',
  contrast_200: '#B5CBE3',
  contrast_300: '#97B4D3',
  contrast_400: '#759AC2',
  contrast_500: '#4F7EB0',
  contrast_600: '#3D6895',
  contrast_700: '#2E537A',
  contrast_800: '#214164',
  contrast_900: '#17304A',
  contrast_950: '#102337',
  contrast_975: '#09192A',
  // Used as the *default* dark theme's background (via invertPalette). Kept
  // near-black (not literally astra's `#0b1e30`) so "dark" stays visually
  // distinct from "dim" below, but still carries the navy hue instead of
  // upstream Bluesky's pure `#000000`.
  contrast_1000: '#040A10',
}

/**
 * Same navy tint applied to the subdued ramp. Its `contrast_1000` becomes
 * the "dim" theme's background (via invertPalette) and lands almost exactly
 * on astrabun.com's own dark bg (`#0b1e30`).
 */
const BUNSKY_SUBDUED_CONTRAST: Pick<
  Palette,
  | 'contrast_25'
  | 'contrast_50'
  | 'contrast_100'
  | 'contrast_200'
  | 'contrast_300'
  | 'contrast_400'
  | 'contrast_500'
  | 'contrast_600'
  | 'contrast_700'
  | 'contrast_800'
  | 'contrast_900'
  | 'contrast_950'
  | 'contrast_975'
  | 'contrast_1000'
> = {
  contrast_25: '#F8FAFC',
  contrast_50: '#EFF5FB',
  contrast_100: '#DDE8F3',
  contrast_200: '#B9CEE4',
  contrast_300: '#9EB9D6',
  contrast_400: '#7B9FC6',
  contrast_500: '#5985B5',
  contrast_600: '#426F9F',
  contrast_700: '#345D89',
  contrast_800: '#274B72',
  contrast_900: '#1D3C5D',
  contrast_950: '#15304C',
  contrast_975: '#102842',
  contrast_1000: '#0C1E31',
}

const BUNSKY_PALETTE: Palette = {
  ...DEFAULT_PALETTE,
  ...BUNSKY_PRIMARY,
  ...BUNSKY_CONTRAST,
  pink: BUNSKY_PINK,
  like: BUNSKY_PINK,
}

const BUNSKY_SUBDUED_PALETTE: Palette = {
  ...DEFAULT_SUBDUED_PALETTE,
  ...BUNSKY_PRIMARY,
  ...BUNSKY_SUBDUED_CONTRAST,
  pink: BUNSKY_PINK,
  like: BUNSKY_PINK,
}

const DEFAULT_THEMES = createThemes({
  defaultPalette: BUNSKY_PALETTE,
  subduedPalette: BUNSKY_SUBDUED_PALETTE,
})

export const themes = {
  lightPalette: DEFAULT_THEMES.light.palette,
  darkPalette: DEFAULT_THEMES.dark.palette,
  dimPalette: DEFAULT_THEMES.dim.palette,
  light: DEFAULT_THEMES.light,
  dark: DEFAULT_THEMES.dark,
  dim: DEFAULT_THEMES.dim,
}

/**
 * @deprecated use ALF and access palette from `useTheme()`
 */
export const lightPalette = DEFAULT_THEMES.light.palette
/**
 * @deprecated use ALF and access palette from `useTheme()`
 */
export const darkPalette = DEFAULT_THEMES.dark.palette
/**
 * @deprecated use ALF and access palette from `useTheme()`
 */
export const dimPalette = DEFAULT_THEMES.dim.palette
/**
 * @deprecated use ALF and access theme from `useTheme()`
 */
export const light = DEFAULT_THEMES.light
/**
 * @deprecated use ALF and access theme from `useTheme()`
 */
export const dark = DEFAULT_THEMES.dark
/**
 * @deprecated use ALF and access theme from `useTheme()`
 */
export const dim = DEFAULT_THEMES.dim
